import { useEffect, useRef, useState, type ReactNode } from "react";
import { api, type Me } from "../../lib/api";
import { CmsSidebar } from "./CmsSidebar";
import { CmsTopBar } from "./CmsTopBar";

interface CmsShellProps {
  route: string;
  user: Me;
  onLogout: () => void | Promise<void>;
  children: ReactNode;
  pageTitle?: string;
}

export function CmsShell({ route, children, pageTitle }: CmsShellProps) {
  const [portfolioUrl, setPortfolioUrl] = useState<string | undefined>(undefined);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const stored = localStorage.getItem("cms-sidebar-collapsed");
    if (stored !== null) return stored === "true";
    return window.matchMedia("(max-width: 899px)").matches;
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.getMyPortfolio(), api.runtimeConfig()])
      .then(([portfolio, cfg]) => {
        if (!cancelled) {
          if (cfg?.subdomain_host) {
            setPortfolioUrl(`https://${portfolio.Slug}.${cfg.subdomain_host}`);
          } else if (cfg?.public_url) {
            setPortfolioUrl(cfg.public_url);
          }
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Close mobile drawer on route change.
  useEffect(() => { setMobileOpen(false); }, [route]);

  function toggleCollapsed() {
    setCollapsed(v => {
      const next = !v;
      localStorage.setItem("cms-sidebar-collapsed", String(next));
      return next;
    });
  }

  function toggleMobile() { setMobileOpen(v => !v); }
  function closeMobile() { setMobileOpen(false); }

  const sidebarWidth = collapsed ? 48 : 240;

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: "var(--bg)" }}>
      <CmsTopBar
        pageTitle={pageTitle}
        portfolioUrl={portfolioUrl}
        onToggleMobile={toggleMobile}
      />

      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* Backdrop for mobile drawer */}
        {mobileOpen && (
          <div
            onClick={closeMobile}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
              zIndex: 199, display: "none",
            }}
            className="cms-backdrop"
          />
        )}

        {/* Sidebar wrapper */}
        <div
          ref={wrapperRef}
          className={`cms-sidebar-wrapper${mobileOpen ? " mobile-open" : ""}`}
          style={{ width: sidebarWidth, flexShrink: 0, transition: "width 0.2s" }}
        >
          <CmsSidebar
            route={route}
            collapsed={collapsed}
            onToggleCollapsed={toggleCollapsed}
          />
        </div>

        {/* Main content */}
        <div style={{ flex: 1, minWidth: 0, padding: "1.5rem", overflowY: "auto" }}>
          {children}
        </div>
      </div>

      <style>{`
        /* tablet: auto-collapse */
        @media (max-width: 899px) {
          .cms-sidebar-wrapper { width: 48px !important; }
        }
        /* mobile: overlay drawer */
        @media (max-width: 599px) {
          .cms-backdrop { display: block !important; }
          .cms-sidebar-wrapper {
            position: fixed !important;
            left: 0; top: 0; height: 100vh;
            z-index: 200;
            width: 240px !important;
            transform: translateX(-100%);
            transition: transform 0.22s ease, width 0s !important;
          }
          .cms-sidebar-wrapper.mobile-open { transform: translateX(0); }
          .cms-menu-btn { display: flex !important; }
        }
      `}</style>
    </div>
  );
}
